import React, { Fragment, useRef } from 'react'
import classnames from 'classnames'
import Img from 'react-image'
import SVG from 'react-inlinesvg'
import medalIcon from '@icons/reward/medal'
import timerIcon from '@icons/reward/timer'
import questionIcon from '@icons/reward/question'
import Platform from '@app/components/Landing/Platform'
import TasksContainer from '@app/containers/students/Tasks'
import Loader from '@app/components/ui/placeholders/Loader'
import PreparatoryRewarding from '@app/components/students/PreparatoryRewarding'
import { Can } from '@app/context/ability-context'
import { maxScore, totalScore, timeSpent } from '@app/helpers/studentsHelper'
import { AwardingPart } from '@app/components/shared/students/LandingPart'
import Modal from '@app/components/ui/modals/Modal'
import ResultLearnModal from "./components/ResultLearnModal";
import ResultBanner, { ResultBannerVariant, ParticipationStatus, getStudentCategory } from "./components/ResultBanner";

const NotParicipated = ({ student, statistics }) => {
  const objects = Object.values(statistics.objects);
  const studentResultBannerVariant = ResultBannerVariant[ParticipationStatus.DID_NOT_PARTICIPATE];

  return (
    <div className="student-tasks-page__reward-inner">
      <ResultBanner
        iconSrc={studentResultBannerVariant.icon}
        title={studentResultBannerVariant.title}
        Footer={studentResultBannerVariant.footer}
      />
      <ResultRow
        iconSrc={medalIcon}
        Content={() => <>Your score <b>{0} out of {maxScore(objects)}</b></>}
      />
      <ResultRow
        iconSrc={timerIcon}
        Content={() => <>Your time <b>{0} minutes</b></>}
      />
    </div>
  )
}

const prizeLocaleKey = (prize) => {
  return {
    diploma: 'download_diplom',
    certificate: 'download_cert',
    charter: 'download_gramota'
  }[prize]
}

const Preview = ({className, modal, participation}) => {
  const classes = classnames("student-tasks-page--award__prize", className)
  return (
    <div className={classes}>
      <div className="student-tasks-page--award__prize--wrapper">
        <Modal ref={modal} className="modal--viewer">
          <Img src={participation.preview} decode={false}/>
        </Modal>
        <Img
          src={participation.preview}
          decode={false}
          loader={<Loader />}
          onClick={() => modal.current.open()}
        />
      </div>
    </div>
  )
}

const Participated = ({ student, statistics, event }) => {
  const { participation } = student
  const objects = Object.values(statistics.objects);
  const modal = useRef(null)
  const time = timeSpent(objects) || 0
  const { participation: { olympiad } } = student
  const studentTotalScore = totalScore(objects);
  const achievementThreshold = olympiad.diploma_score
  const meritThreshold = olympiad.charter_score
  const studentResultBannerVariant = ResultBannerVariant[getStudentCategory({studentTotalScore, achievementThreshold, meritThreshold})]

  return (
    <div className="student-tasks-page__reward-inner">
      <ResultBanner
        iconSrc={studentResultBannerVariant.icon}
        title={studentResultBannerVariant.title}
        Footer={studentResultBannerVariant.footer}
        participation={participation}
        event={event}
      />
      <ResultRow
        iconSrc={medalIcon}
        Content={() => <>Your score <b>{totalScore(objects)} out of {maxScore(objects)}</b></>}
      />
      <ResultRow
        iconSrc={timerIcon}
        Content={() => <>Your time <b>{Math.floor(time / 60)} minutes</b></>}
      />
    </div>
  )
}

const ResultRow = ({iconSrc, Content}) => {
  return (
    <div className="student-tasks-page__result-row">
      <div className="student-tasks-page__result-icon">
        <SVG src={iconSrc} />
      </div>
      <div className="student-tasks-page__result">
        <Content />
      </div>
    </div>
  )
}

const ResultLearnButton = ({ onClick }) => {
  return (
    <div className="student-tasks-page__learn-rewards" onClick={onClick}>
      <div className="student-tasks-page__learn-rewards-icon">
        <SVG src={questionIcon} width="90" height="90" />
      </div>
      <div className="student-tasks-page__learn-rewards-label">
        LEARN ABOUT REWARDS
      </div>
    </div>
  )
}

const TrialMainRewarding = ({student, global}) => {
  const resultLearnModal = useRef(null);
  const classes = classnames(
    'student-tasks-page',
    'student-tasks-page--rewarding'
  )
  const { event } = global;

  const handleOpenModal = () => {
    resultLearnModal.current.open();
  };

  const handleCloseModal = () => {
    resultLearnModal.current.close();
  };

  return (
    <Fragment>
      <div className={classes}>
        <div className="student-tasks-page">
          <div className="student-tasks-page__reward-wrapper">
            <div className="student-tasks-page__reward-inner" style={{ maxWidth: '480px' }}>
              <div className="student-tasks-page__panel">
                {
                  student.isParticipated ? (
                    <Can I="read" of="Award" passThrough>
                      {
                        can => can ? (
                          <>
                            <TasksContainer
                              student={student}
                              tour="main"
                              ChildComponent={(props) => <Participated {...props} event={event} />}
                            />
                            <ResultLearnButton onClick={handleOpenModal} />
                          </>
                        ) : (
                          <div className="f--default f--l" style={{ color: 'white', textAlign: 'center' }}>
                            { I18n.t('awards.will_be_available') }
                          </div>
                        )
                      }
                    </Can>
                  ) : (
                    <TasksContainer
                      student={student}
                      tour="main"
                      ChildComponent={NotParicipated}
                    />
                  )
                }
              </div>
              <Platform className="pt-1" theme="white"/>
            </div>
            </div>
        </div>
      </div>
      <AwardingPart student={student} global={global}/>
      <ResultLearnModal
        student={student}
        modal={resultLearnModal}
        onClose={handleCloseModal}
      />
    </Fragment>
  )
}

const Rewarding = ({student, global}) => {
  const {event} = global;

  return (
    <Fragment>
      {event.afterPreparatoryTour ? (
        <PreparatoryRewarding student={student} global={global}/>
      ) : (
        <TrialMainRewarding student={student} global={global}/>
      )
      }
    </Fragment>
  )
}

export default Rewarding
